import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import store from './store/store';
import { BrowserRouter as Router } from 'react-router-dom';
import 'antd/dist/reset.css';

import AppRoutes from './routes';
import './App.css';
import { photoSyncService } from './services/photoSyncService';
import i18n from './i18n';
import { apiSlice } from './api/apiSlice';
import { bulkOperationsApi } from './api/bulkOperationsApi';

// Инициализация сервиса синхронизации фото
const initializePhotoSync = () => {
  // Функция загрузки фото на сервер через RTK Query
  const uploadPhotoToServer = async (photo) => {
    if (!photo.metadata?.saveId) {
      throw new Error('saveId is missing for photo sync');
    }
    // Преобразуем Blob в File для правильной отправки
    const file = new File([photo.blob], photo.fileName, { type: photo.mimeType });
    
    // Используем API из store через dispatch
    const { plantingsApi } = await import('./api/plantingsApi');
    
    const result = await store.dispatch(
      plantingsApi.endpoints.uploadPlantingPhoto.initiate({
        saveId: photo.metadata?.saveId,
        plantingId: photo.plantingId,
        file: file,
        description: photo.metadata?.description,
      })
    );

    if (result.error) {
      // Если 403 - недостаточно прав, прокидываем специальную ошибку
      if (result.error.status === 403) {
        const error = new Error(result.error.data?.message || i18n.t('layout:photoSync.uploadForbidden'));
        error.isForbidden = true;
        throw error;
      }
      throw new Error(result.error.data?.message || 'Upload failed');
    }

    return result.data?.url || result.data?.photo?.url || '';
  };

  photoSyncService.initialize(uploadPhotoToServer);
};

function AppWithSync() {
  useEffect(() => {
    initializePhotoSync();
    
    return () => {
      photoSyncService.destroy();
    };
  }, []);

  // Server responses are localized by Accept-Language: drop cached ones when the
  // UI language changes, mounted queries refetch in the new language.
  useEffect(() => {
    let language = i18n.language;
    const resetCaches = (next) => {
      if (next === language) return;
      language = next;
      store.dispatch(apiSlice.util.resetApiState());
      store.dispatch(bulkOperationsApi.util.resetApiState());
    };
    i18n.on('languageChanged', resetCaches);
    return () => i18n.off('languageChanged', resetCaches);
  }, []);

  return <AppRoutes />;
}

function App() {
  return (
    <Provider store={store}>
      <Router>
        <AppWithSync />
      </Router>
    </Provider>
  );
}

export default App;
